import { COMPONENT_HEIGHTS, Colors, DateInput, Icon, SelectCard, Text } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { ALL_EVALUATORS, ALL_PERIODS, CUSTOM_PERIOD } from './skillsetPeriodIds';
import { useSkillsetEvaluationPeriods } from './useSkillsetEvaluationPeriods';

import type { SkillsetPeriod } from './skillsetPeriodIds';
import type { EvaluationPeriod } from '@/types/manager-assessment/Evaluation';

import { useGetSkillsetEvaluators } from '@/api/performance-profile';
import { parseIsoDate, toDateInputLang, toIsoDate } from '@/utils/dateUtils';
import { formatDayRange } from '@/utils/dayRange';

/**
 * One height for every control of the filter row -- the two pickers and, once custom dates are
 * chosen, the two date fields -- so the row reads as a single band.
 */
const FILTER_CONTROL_HEIGHT = COMPONENT_HEIGHTS.mdPlus;

/**
 * The card's default pale violet edge is dropped here: the border stays, transparent, so the
 * pickers keep their height and still line up with the date fields.
 */
const CARD_BORDER = 'border border-transparent';
const CARD_HOVER_BORDER = 'hover:border-transparent';

const PREDEFINED_PERIODS_HEADER = 'predefined-filters-header';

// The filter row is where callers expect to find what its menus answer with, so the ids keep
// reading from here; they are declared apart so the helpers may share them without a cycle.
export { ALL_EVALUATORS, ALL_PERIODS, CUSTOM_PERIOD };
export type { SkillsetPeriod };

interface SkillsetAssessmentFiltersProps {
    employeeId: string;
    /** An evaluator id, or {@link ALL_EVALUATORS}. */
    evaluatorId: string;
    onEvaluatorChange: (evaluatorId: string) => void;
    period: SkillsetPeriod;
    onPeriodChange: (period: SkillsetPeriod) => void;
}

/**
 * The two menus above the skillset assessment: who assessed, then when. A predefined period brings
 * its own dates; the custom one shows the two date fields.
 */
export const SkillsetAssessmentFilters = ({
    employeeId,
    evaluatorId,
    onEvaluatorChange,
    period,
    onPeriodChange,
}: SkillsetAssessmentFiltersProps) => {
    const { t, i18n } = useTranslation();
    const lang = toDateInputLang(i18n.language);
    const { data: evaluators } = useGetSkillsetEvaluators(employeeId);
    const { periods: evaluationPeriods } = useSkillsetEvaluationPeriods(employeeId);

    const evaluatorOptions = [
        {
            value: ALL_EVALUATORS,
            label: t('performanceProfile.skillsetAssessment.allEvaluations'),
        },
        ...(evaluators ?? []).map((evaluator) => ({ value: evaluator.id, label: evaluator.name })),
    ];

    // What sits under a period's name: the days it spans, and the note that it is the one running
    // today. A period the setup vocabulary did not name is already called by those days, so
    // repeating them under itself would say the same thing twice.
    //
    // Read in the very language the name was built in, `i18n.language` -- not the two-language code
    // `DateInput` takes: any other reading would spell the same span differently and make a period
    // repeat itself under its own name.
    const describePeriodOption = (evaluationPeriod: EvaluationPeriod) => {
        const span = formatDayRange(
            evaluationPeriod.startDate,
            evaluationPeriod.endDate,
            i18n.language,
        );
        const parts = evaluationPeriod.name === span ? [] : [span];

        if (evaluationPeriod.isCurrent) {
            parts.push(t('performanceProfile.skillsetAssessment.currentCycle'));
        }

        return parts.join(' · ');
    };

    const periodOptions = [
        {
            value: CUSTOM_PERIOD,
            label: t('performanceProfile.skillsetAssessment.customPeriod'),
        },
        {
            value: PREDEFINED_PERIODS_HEADER,
            label: '',
            disabled: true,
            description: (
                <span className={'text-base-400 text-sm font-semibold tracking-wider uppercase'}>
                    {t('performanceProfile.skillsetAssessment.predefinedFilters')}
                </span>
            ),
        },
        { value: ALL_PERIODS, label: t('performanceProfile.skillsetAssessment.allCycles') },
        ...(evaluationPeriods ?? []).map((evaluationPeriod) => ({
            value: evaluationPeriod.id,
            label: evaluationPeriod.name,
            description: describePeriodOption(evaluationPeriod),
        })),
    ];

    const handlePeriodChange = (filterId: string) => {
        const predefined = evaluationPeriods?.find(
            (evaluationPeriod) => evaluationPeriod.id === filterId,
        );
        onPeriodChange(
            predefined
                ? {
                      filterId,
                      fromDate: parseIsoDate(predefined.startDate) ?? period.fromDate,
                      toDate: parseIsoDate(predefined.endDate) ?? period.toDate,
                  }
                : { ...period, filterId },
        );
    };

    // Moving the start past the end carries the end along instead of refusing the date: a period
    // whose bounds cross matches no evaluation, and every competency would read as unassessed.
    // Compared by calendar day, so a one-day period is left alone.
    const handleFromDateChange = (fromDate: Date) => {
        const toDate = toIsoDate(fromDate) > toIsoDate(period.toDate) ? fromDate : period.toDate;
        onPeriodChange({ ...period, fromDate, toDate });
    };

    return (
        <div className={'gap-gap-md mt-4 mb-2 flex flex-wrap items-end'}>
            {/* Shut until the evaluators are known, or when they could not be read: opened, the
                menu would offer "All evaluations" alone and read as a single assessor. */}
            <SelectCard
                options={evaluatorOptions}
                value={evaluatorId}
                onChange={onEvaluatorChange}
                disabled={!evaluators}
                sort={'none'}
                triggerBorderClassName={CARD_BORDER}
                triggerHoverBorderClassName={CARD_HOVER_BORDER}
                placement={'bottom-left'}
                ariaLabel={t('performanceProfile.skillsetAssessment.evaluatorFilter')}
                leadingIcon={
                    <Icon
                        name={'users-profile-circle'}
                        size={'sm'}
                        colorClassName={'text-obj-slate'}
                        ariaHidden
                    />
                }
                dataTestId={'skillset-assessment-evaluator-select'}
            />

            <SelectCard
                options={periodOptions}
                value={period.filterId}
                onChange={handlePeriodChange}
                sort={'none'}
                triggerBorderClassName={CARD_BORDER}
                triggerHoverBorderClassName={CARD_HOVER_BORDER}
                placement={'bottom-left'}
                ariaLabel={t('performanceProfile.skillsetAssessment.periodFilter')}
                dataTestId={'skillset-assessment-period-select'}
            />

            {period.filterId === CUSTOM_PERIOD && (
                <div className={'flex flex-wrap items-center gap-3'}>
                    <Text
                        size={'small'}
                        className={'text-obj-gray uppercase'}
                    >
                        {t('performanceProfile.skillsetAssessment.from')}
                    </Text>
                    <div className={'w-48'}>
                        <DateInput
                            value={period.fromDate}
                            onChange={(date) => date && handleFromDateChange(date)}
                            lang={lang}
                            placeholder={t('performanceProfile.skillsetAssessment.periodFrom')}
                            clearable={false}
                            popoverClassName={'z-[9999]'}
                            inputClassName={'!py-1.5 !text-sm whitespace-nowrap'}
                            heightClassName={FILTER_CONTROL_HEIGHT}
                            dataTestId={'skillset-assessment-period-from'}
                        />
                    </div>

                    <Icon
                        name={'arrow-arrow-right'}
                        size={16}
                        color={Colors.BASE_300}
                    />

                    <Text
                        size={'small'}
                        className={'text-obj-gray uppercase'}
                    >
                        {t('performanceProfile.skillsetAssessment.to')}
                    </Text>
                    <div className={'w-48'}>
                        <DateInput
                            value={period.toDate}
                            onChange={(date) => date && onPeriodChange({ ...period, toDate: date })}
                            disabledDates={{ before: period.fromDate }}
                            lang={lang}
                            placeholder={t('performanceProfile.skillsetAssessment.periodTo')}
                            clearable={false}
                            popoverClassName={'z-[9999]'}
                            inputClassName={'!py-1.5 !text-sm whitespace-nowrap'}
                            heightClassName={FILTER_CONTROL_HEIGHT}
                            dataTestId={'skillset-assessment-period-to'}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
